import { RiSnowflakeFill, RiSnowflakeLine } from "@remixicon/react";
import type { DensityStateView } from "@screener/protocol";
import { Badge } from "@screener/uikit/components/badge";
import { Button } from "@screener/uikit/components/button";
import { RollingNumber } from "@screener/uikit/components/rolling-number";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import { memo } from "react";
import { formatCount } from "@/lib/labels";
import { type ScreenCountsStore, useScreenCount } from "./screen-counts";

/**
 * Листья со счётчиками полосы фильтров: каждый читает своё число и один
 * перерисовывается, когда оно меняется. Экранные компоненты, не UIKit: знают
 * про стадии и биржи.
 */

/** «N из M» — сколько строк показано из скольких. */
export function VisibleOfTotal({ store }: { store: ScreenCountsStore }) {
  const visible = useScreenCount(store, (counts) => counts.visible);
  const total = useScreenCount(store, (counts) => counts.total);
  return (
    // `data-live-leaf` нужен проверке изоляции перерисовок (e2e): только листья
    // с этим признаком вправе перерисовываться в полосе, шапке и панели.
    <span data-live-leaf className="pl-2 text-[12px] font-medium whitespace-nowrap tabular-nums">
      <RollingNumber value={visible} format={formatCount} />
      {/* Приглушённое « из M» — часть того же листа: меняется вместе с M. */}
      <span data-live-leaf className="text-muted-foreground">
        {" из "}
        <RollingNumber value={total} format={formatCount} />
      </span>
    </span>
  );
}

/**
 * Колонка чисел панели: цифры одной ширины и место на пять знаков, чтобы
 * смена разрядности («1 116» → «998») не сдвигала соседние подписи.
 */
const COUNT_COLUMN = "min-w-[5ch] text-xs text-muted-foreground tabular-nums";

/** Число плотностей стадии на карточке отбора. */
export function StageCount({
  store,
  stage,
}: {
  store: ScreenCountsStore;
  stage: DensityStateView;
}) {
  const count = useScreenCount(store, (counts) => counts.states[stage] ?? 0);
  return (
    <span data-live-leaf className={cn(COUNT_COLUMN, "text-left")}>
      <RollingNumber value={count} format={formatCount} />
    </span>
  );
}

/**
 * Доступное имя кнопки заморозки: счёт новых плотностей назван словами, а не
 * только значком (цветом и числом на значке ничего не сообщается в одиночку).
 */
function freezeButtonLabel(frozen: boolean, newCount: number): string {
  if (!frozen) {
    return "Заморозить";
  }
  return newCount > 0 ? `Разморозить, новых: ${newCount}` : "Разморозить";
}

/** Снежинка кнопки заморозки: меняется только по действию человека, не по счёту новых. */
const FreezeIcon = memo(function FreezeIcon({ frozen }: { frozen: boolean }) {
  return frozen ? (
    <RiSnowflakeFill aria-hidden="true" className="size-3.5" />
  ) : (
    <RiSnowflakeLine aria-hidden="true" className="size-3.5" />
  );
});

/**
 * Классы кнопки заморозки по хендоффу §113: обычная — прозрачный фон и рамка
 * `--border`; нажатая — фон `--accent` и рамка `--ring`; цвет меняется за
 * 100 мс линейно. Hover нажатой не перекрашивает её (иначе в тёмной теме её
 * забивал бы `muted`).
 */
export function freezeButtonClass(frozen: boolean): string {
  return cn(
    "relative size-[26px] overflow-visible rounded-sm border text-foreground transition-colors duration-100 ease-linear",
    frozen
      ? "border-ring bg-accent hover:bg-accent dark:hover:bg-accent"
      : "border-border bg-transparent dark:bg-transparent",
  );
}

/** Значок со счётом плотностей, пришедших после заморозки; при нуле не рисуется. */
function FrozenNewCount({ frozen, count }: { frozen: boolean; count: number }) {
  if (!frozen || count === 0) {
    return null;
  }
  return (
    <Badge
      data-slot="freeze-new-count"
      data-live-leaf
      aria-hidden="true"
      size="count"
      className="absolute -top-2 -right-2"
    >
      <RollingNumber value={count} />
    </Badge>
  );
}

/**
 * Кнопка заморозки вида «Карточки» §77. Число новых плотностей приходит из
 * хранилища счётчиков, а не пропсом полосы: рождение плотности при заморозке
 * перерисовывает эту кнопку и значок на ней, но не фишки, сортировку и прочее
 * в полосе. Кнопка сама — живой лист: её доступное имя несёт счёт.
 */
export function FreezeToggle({
  store,
  frozen,
  onToggle,
}: {
  store: ScreenCountsStore;
  frozen: boolean;
  onToggle: () => void;
}) {
  const newCount = useScreenCount(store, (counts) => counts.newCount);
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            data-live-leaf
            variant="ghost"
            size="icon-sm"
            onClick={onToggle}
            aria-label={freezeButtonLabel(frozen, newCount)}
            aria-pressed={frozen}
            className={freezeButtonClass(frozen)}
          />
        }
      >
        <FreezeIcon frozen={frozen} />
        <FrozenNewCount frozen={frozen} count={newCount} />
      </TooltipTrigger>
      <TooltipContent>{frozen ? "Разморозить" : "Заморозить"}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Число плотностей биржи в строке отбора: сумма по перечисленным ключам рынков
 * «<биржа>:<рынок>» (§115 п. 3). Какие рынки считать — решает строка, лист
 * только читает числа потока, и строка от потока не перерисовывается.
 */
export function VenueCount({
  store,
  marketKeys,
  className,
}: {
  store: ScreenCountsStore;
  marketKeys: readonly string[];
  className?: string;
}) {
  const count = useScreenCount(store, (counts) =>
    marketKeys.reduce((sum, key) => sum + (counts.venues[key] ?? 0), 0),
  );
  return (
    <span
      data-live-leaf
      data-empty={count === 0}
      className={cn(COUNT_COLUMN, "text-right", className)}
    >
      <RollingNumber value={count} format={formatCount} />
    </span>
  );
}
